<template>
  <a-card class="birthday-card mb-3">
    <template #title>
      <h2 class="birthday-card__title">Upcoming birthdays</h2>
    </template>

    <template #extra>
      <img class="birthday-card__image" src="/birthday.png" alt="" aria-hidden="true" />
    </template>

    <div v-if="hasBirthdayToday" class="birthday-card__confetti-layer" aria-hidden="true">
      <span v-for="piece in CONFETTI_PIECES" :key="piece" class="birthday-card__confetti-piece" />
    </div>

    <a-list :data-source="sortedBirthdays" item-layout="horizontal" class="birthday-list">
      <template #renderItem="person">
        <a-list-item>
          <a-list-item-meta>
            <template #avatar>
              <a-avatar
                :size="42"
                :src="person.avatar"
                :alt="person.name"
                :class="{ 'birthday-avatar--initials': !person.avatar }"
              >
              </a-avatar>
            </template>

            <template #title>
              <div class="birthday-person__top">
                <span class="birthday-person__name">
                  {{ person.name }}
                </span>

                <a-tag
                  :color="person.daysUntil === 0 ? 'purple' : undefined"
                  class="birthday-badge"
                  :class="{ 'birthday-badge--today': person.daysUntil === 0 }"
                >
                  {{ getRelativeLabel(person.daysUntil) }}
                </a-tag>
              </div>
            </template>

            <template #description>
              <span>{{ formatBirthday(person.nextBirthday) }}</span>
              <a-divider type="vertical" />
              <span>Turns {{ person.turningAge }}</span>
            </template>
          </a-list-item-meta>
        </a-list-item>
      </template>
    </a-list>
  </a-card>
</template>

<script setup lang="ts">
import { UserBirthday } from "@/modules/authentication/types";
import { computed } from "vue";

const props = defineProps({
  birthdays: {
    type: Array<UserBirthday>,
    default: () => [],
  },
});

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const CONFETTI_PIECES = 14;

const sortedBirthdays = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return props.birthdays
    .map((person) => {
      const birthDate = parseDate(person.birth_date);

      let nextBirthday = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());

      if (nextBirthday < today) {
        nextBirthday = new Date(today.getFullYear() + 1, birthDate.getMonth(), birthDate.getDate());
      }

      const daysUntil = Math.round((nextBirthday.getTime() - today.getTime()) / MS_PER_DAY);

      const turningAge = nextBirthday.getFullYear() - birthDate.getFullYear();

      return {
        ...person,
        nextBirthday,
        daysUntil,
        turningAge,
      };
    })
    .sort((a, b) => Number(a.nextBirthday) - Number(b.nextBirthday));
});

const hasBirthdayToday = computed(() =>
  sortedBirthdays.value.some((person) => person.daysUntil === 0)
);

function parseDate(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatBirthday(date: Date) {
  return new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "short",
  }).format(date);
}

function getRelativeLabel(days: number) {
  if (days === 0) return "Today 🎉";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}
</script>

<style lang="scss" scoped>
@use "@/assets/scss/colors.module" as colors;

.birthday-card {
  position: relative;
  overflow: hidden;
  border-color: #eadcff;
  border-radius: 8px;
  background: linear-gradient(135deg, colors.$white 0%, #fcf9ff 55%, #f5edff 100%);
  box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.2);

  &::after {
    position: absolute;
    right: -32px;
    bottom: -46px;
    width: 110px;
    height: 110px;
    border-radius: 50%;
    background: rgba(colors.$cyan, 0.12);
    content: "";
    pointer-events: none;
  }
}

.birthday-card :deep(.ant-card-head) {
  position: relative;
  z-index: 1;
  border-bottom-color: rgba(colors.$purple, 0.14);
  background: transparent;
}

.birthday-card :deep(.ant-card-body) {
  background: transparent;
}

.birthday-card__image {
  width: 40%;
  position: absolute;
  top: 10%;
  right: 5%;
  z-index: -1;
  opacity: 0.2;
}

.birthday-card__confetti-layer {
  position: absolute;
  z-index: 2;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.birthday-card__confetti-piece {
  position: absolute;
  top: -14px;
  left: var(--confetti-left);
  width: 7px;
  height: 12px;
  border-radius: 2px;
  background: var(--confetti-color);
  opacity: 0;
  animation: birthday-confetti-fall var(--confetti-duration) ease-in var(--confetti-delay) infinite
    both;

  &:nth-child(1) {
    --confetti-left: 5%;
    --confetti-color: #{colors.$purple};
    --confetti-delay: 0s;
    --confetti-duration: 1.8s;
    --confetti-drift: 22px;
  }

  &:nth-child(2) {
    --confetti-left: 13%;
    --confetti-color: #{colors.$cyan};
    --confetti-delay: 0.3s;
    --confetti-duration: 2.2s;
    --confetti-drift: -14px;
  }

  &:nth-child(3) {
    --confetti-left: 21%;
    --confetti-color: #{colors.$pink};
    --confetti-delay: 0.1s;
    --confetti-duration: 2s;
    --confetti-drift: 18px;
  }

  &:nth-child(4) {
    --confetti-left: 29%;
    --confetti-color: #{colors.$yellow};
    --confetti-delay: 0.55s;
    --confetti-duration: 1.9s;
    --confetti-drift: -20px;
  }

  &:nth-child(5) {
    --confetti-left: 37%;
    --confetti-color: #{colors.$purple};
    --confetti-delay: 0.2s;
    --confetti-duration: 2.3s;
    --confetti-drift: 12px;
  }

  &:nth-child(6) {
    --confetti-left: 44%;
    --confetti-color: #{colors.$cyan};
    --confetti-delay: 0.65s;
    --confetti-duration: 1.8s;
    --confetti-drift: -16px;
  }

  &:nth-child(7) {
    --confetti-left: 51%;
    --confetti-color: #{colors.$pink};
    --confetti-delay: 0.05s;
    --confetti-duration: 2.1s;
    --confetti-drift: 24px;
  }

  &:nth-child(8) {
    --confetti-left: 58%;
    --confetti-color: #{colors.$yellow};
    --confetti-delay: 0.4s;
    --confetti-duration: 2.4s;
    --confetti-drift: -12px;
  }

  &:nth-child(9) {
    --confetti-left: 65%;
    --confetti-color: #{colors.$purple};
    --confetti-delay: 0.15s;
    --confetti-duration: 1.9s;
    --confetti-drift: 16px;
  }

  &:nth-child(10) {
    --confetti-left: 72%;
    --confetti-color: #{colors.$cyan};
    --confetti-delay: 0.7s;
    --confetti-duration: 2.2s;
    --confetti-drift: -22px;
  }

  &:nth-child(11) {
    --confetti-left: 79%;
    --confetti-color: #{colors.$pink};
    --confetti-delay: 0.25s;
    --confetti-duration: 2s;
    --confetti-drift: 14px;
  }

  &:nth-child(12) {
    --confetti-left: 85%;
    --confetti-color: #{colors.$yellow};
    --confetti-delay: 0.5s;
    --confetti-duration: 1.8s;
    --confetti-drift: -18px;
  }

  &:nth-child(13) {
    --confetti-left: 91%;
    --confetti-color: #{colors.$purple};
    --confetti-delay: 0.1s;
    --confetti-duration: 2.3s;
    --confetti-drift: 10px;
  }

  &:nth-child(14) {
    --confetti-left: 96%;
    --confetti-color: #{colors.$cyan};
    --confetti-delay: 0.6s;
    --confetti-duration: 2s;
    --confetti-drift: -24px;
  }

  &:nth-child(3n) {
    width: 9px;
    height: 9px;
    border-radius: 50%;
  }
}

@keyframes birthday-confetti-fall {
  0% {
    opacity: 0;
    transform: translate3d(0, -16px, 0) rotate(0deg);
  }

  12% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translate3d(var(--confetti-drift), 320px, 0) rotate(720deg);
  }
}

.birthday-card__title {
  margin: 0;
  font-size: 20px;
}

.birthday-list {
  position: relative;
  z-index: 1;
}

.birthday-avatar--initials {
  background: colors.$grey_lighter;
  color: colors.$blue_darker;
  font-weight: 700;
}

.birthday-person__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.birthday-person__name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  font-size: 15px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.birthday-badge {
  flex: 0 0 auto;
  margin-right: 0;
}

.birthday-badge--today {
  padding: 1px 8px;
  font-size: 13px;
}

@media (prefers-reduced-motion: reduce) {
  .birthday-card__confetti-layer {
    display: none;
  }
}
</style>
